'use client'

import { useAuthenticationModalStore, useAuthenticationStore } from "@/core/stores/auth";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import PinInput from 'react-pin-input';
import Cookies from 'js-cookie';
import { useItineraryUserStore, useUserStore } from '@/core/stores/user';
import { decodeToken } from '@/utils/deCodeJwtToken';
import { getProfile } from '@/actions/authentication';
import useAppStore from '@/core/stores/app';
import Button from '../ui/Button';

export default function VerificationCodeForm({ setShowCodeInput, onSubmit }: { setShowCodeInput: Dispatch<SetStateAction<boolean>>, onSubmit?: () => void }) {
    const { refresh } = useRouter()
    const { email } = useAuthenticationStore()
    const [second, setSecond] = useState(60)
    const [value, setValue] = useState('');
    const [loading, setLoading] = useState(false)
    const [resendLoading, setResendLoading] = useState(false)
    const [error, setError] = useState('')
    const { setShowAuthModal } = useAuthenticationModalStore()
    const { setItineraryUser } = useItineraryUserStore()
    const { setUserProfile, fetchUserProfile } = useUserStore()

    const { referralCode } = useAppStore()
    const path = usePathname()
    const [verifyError, setVerifyError] = useState('')

    const handleGetUserProfile = async () => {
        const res = await getProfile()

        const { result, response } = JSON.parse(res)

        if (response.ok) {
            if (path.includes('/itineraries')) {
                setItineraryUser(result)
            }
            setUserProfile(result)
        } else {
        }
    }

    const handleRequest = async (value: string) => {
        setLoading(true)

        const body: any = {
            email: email,
            code: +value,
        }

        if (referralCode) {
            body.referralCode = referralCode
        }

        const res = await fetch("/api/auth/verification", {
            method: "POST",
            body: JSON.stringify({ ...body })
        })

        const { result, response } = await res.json();

        if (res.status === 200) {
            Cookies.set('ut', result.token, {
                expires: 30 // One month
            })
            if (onSubmit) {
                onSubmit()
            }
            fetchUserProfile()
            setShowCodeInput(false)
            setShowAuthModal(false)
            refresh()
        } else {
            if (typeof result === 'string') {
                setVerifyError(result)
            } else {
                setVerifyError('verification failed')
            }
        }
        setLoading(false)
    }

    const handleResendEmail = async () => {
        setResendLoading(true)
        const body = {
            email: email
        }
        const res = await fetch("/api/auth/sendEmail", {
            method: "POST",
            body: JSON.stringify({ ...body })
        })
        const response = await res.json();
        if (res.status === 200) {
            setSecond(60)
        } else {
            setError(response.message)
            setResendLoading(false)
        }
    }

    const handleEditEmail = () => {
        setShowCodeInput(false)
    }

    useEffect(() => {
        const interval = setInterval(() => {
            setSecond((prev) => prev > 0 ? prev - 1 : 0)
        }, 1000)
        return () => {
            clearInterval(interval)
        }
    }, [])


    return (
        <div>
            <div className="pb-2">
                <h4 className="font-semibold text-xl text-gray-900 pb-2">Verify your email</h4>
                <p className="text-xs text-gray-500">
                    Enter the code sent to your email here
                </p>
            </div>
            <div className="w-full mx-auto">
                <PinInput
                    length={5}
                    initialValue={value}
                    onChange={(value) => {
                        setVerifyError('')
                        setValue(value)
                    }}
                    type="numeric"
                    inputMode="number"
                    style={{ display: 'grid', gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr", gap: '24px' }}
                    inputStyle={{ borderColor: error ? '#E33F4C80' : '#6b728040', borderRadius: '12px', width: '100%', height: '60px', fontSize: '36px', fontWeight: '800' }}
                    inputFocusStyle={{ borderColor: loading ? 'gray' : '#E33F4C' }}
                    onComplete={(value) => { handleRequest(value) }}
                    autoSelect={true}
                    disabled={loading}
                />
                {verifyError && <p className='text-rose-400 mt-1 text-sm'>
                    {verifyError}
                </p>}
            </div>
            <p className="font-normal text-sm text-gray-500 pt-2">
                We emailed you a five-digit code to {" "}
                <Link className="font-semibold text-sm text-gray-900" href={`mailto:${email}`}>
                    {email}
                </Link>
            </p>
            <div className="font-normal text-gray-500 text-sm pt-2">
                <span>(If this is not your email, click to {" "}</span>
                <button onClick={handleEditEmail} className="text-indigo-500 underline"> Edit Email</button>
                )
            </div>
            <div className="my-6 font-normal text-gray-500 text-sm">
                <span> Didn’t receive a code?{" "}</span>
                {second === 0 ?
                    <button onClick={handleResendEmail} className="text-indigo-500 underline"> {resendLoading ?
                        <div role="status">
                            <svg aria-hidden="true" className="inline w-4 h-4 text-gray-200 animate-spin  fill-gray-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
                                <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
                            </svg>
                            <span className="sr-only">Loading...</span>
                        </div> : 'Resend'}</button>
                    : <span className="text-gray-700 underline"> resend after {second}s</span>
                }
            </div>
            <Button color='secondary' radius='rounded-full'
                className='w-full'
                onClick={() => handleRequest(value)}
                isLoading={loading} >
                {<span>Sign in</span>
                }
            </Button>
            {error && <p className="text-rose-500 text-sm">{error}</p>}
        </div>
    );
};
